// noinspection JSUnusedGlobalSymbols

'use client'

import type { VariantProps } from '@gluestack-ui/nativewind-utils'
import { tva } from '@gluestack-ui/nativewind-utils/tva'
import { withStyleContext } from '@gluestack-ui/nativewind-utils/withStyleContext'
import { createTooltip } from '@gluestack-ui/tooltip'
import { AnimatePresence, Motion } from '@legendapp/motion'
import { cssInterop } from 'nativewind'
import React from 'react'
import { Text, View } from 'react-native'

export const UITooltip = createTooltip({
  // @ts-ignore
  Root: withStyleContext(View),
  // @ts-ignore
  Content: Motion.View,
  Text: Text,
  AnimatePresence: AnimatePresence
})

// @ts-ignore
cssInterop(Motion.View, { className: 'style' })

const tooltipStyle = tva({
  base: 'h-full w-full web:pointer-events-none'
})

const tooltipContentStyle = tva({
  base: 'rounded-sm bg-background-900 px-3 py-1 web:pointer-events-auto'
})

const tooltipTextStyle = tva({
  base: 'font-poppinsRegular text-xs tracking-normal text-typography-50 web:select-none',

  variants: {
    isTruncated: {
      true: {
        props: 'line-clamp-1 truncate'
      }
    },
    bold: {
      true: 'font-poppinsBold'
    },
    underline: {
      true: 'underline'
    },
    strikeThrough: {
      true: 'line-through'
    },
    size: {
      '2xs': 'text-2xs',
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl'
    },
    sub: {
      true: 'text-xs'
    },
    italic: {
      true: 'italic'
    },
    highlight: {
      true: 'bg-yellow-500'
    }
  }
})

type ITooltipProps = React.ComponentProps<typeof UITooltip> & VariantProps<typeof tooltipStyle> & { className?: string }
type ITooltipContentProps = React.ComponentProps<typeof UITooltip.Content> &
  VariantProps<typeof tooltipContentStyle> & { className?: string }
type ITooltipTextProps = React.ComponentProps<typeof UITooltip.Text> &
  VariantProps<typeof tooltipTextStyle> & { className?: string }

const Tooltip = React.forwardRef<React.ComponentRef<typeof UITooltip>, ITooltipProps>(
  ({ className, ...props }, ref) => {
    return (
      <UITooltip
        ref={ref}
        // @ts-ignore
        className={tooltipStyle({ class: className })}
        {...props}
      />
    )
  }
)

const TooltipContent = React.forwardRef<
  React.ComponentRef<typeof UITooltip.Content>,
  ITooltipContentProps & { className?: string }
>(({ className, ...props }, ref) => {
  return (
    <UITooltip.Content
      ref={ref}
      {...props}
      className={tooltipContentStyle({
        class: className
      })}
      pointerEvents='auto'
    />
  )
})

const TooltipText = React.forwardRef<
  React.ComponentRef<typeof UITooltip.Text>,
  ITooltipTextProps & { className?: string }
>(({ size, className, ...props }, ref) => {
  return (
    <UITooltip.Text
      className={tooltipTextStyle({ size, class: className })}
      // maxFontSizeMultiplier={1.5}
      // minimumFontScale={0.5}
      ref={ref}
      {...props}
    />
  )
})

Tooltip.displayName = 'Tooltip'
TooltipContent.displayName = 'TooltipContent'
TooltipText.displayName = 'TooltipText'

export { Tooltip, TooltipContent, TooltipText }
